import { uid } from '../../../../lib/uid';
import type {
  RequestableItem,
  RequestableItemType,
  RequisitionSlipLineInput,
} from '../requisitionSlipTypes';
import { LINE_PREFERENCE } from '../requisitionSlipTypes';

/**
 * A plausible requisition slip, filled from the LIVE feeds.
 *
 * **It takes the catalogs as an argument**, because a parameterless generator cannot pick a
 * real row and silently fills none of the foreign keys — which is how a generated form comes
 * out looking complete and is refused on save.
 *
 * **Nothing it produces reaches `rsys_pii`.** A purpose and a note are prose about supplies, and
 * the accountable employee is not the generator's to fill: the server stamps the requester.
 *
 * **It writes BOTH preferences** — an exact item and a type alone — so one click exercises the
 * item cell and the Type path the profile and the approval panel must render.
 */
export function sampleRequisitionSlip({
  items,
  itemTypes,
}: {
  items: RequestableItem[];
  itemTypes: RequestableItemType[];
}): {
  purpose: string;
  notes: string;
  items: RequisitionSlipLineInput[];
} {
  const purposes = [
    'Replenishing the counter stock the office ran down this month',
    'Preparing the materials the quarterly audit walkthrough needs',
    'Replacing supplies used up during the relocation',
    'Stocking the new workstations before the intake starts',
  ];

  /*
    THE SUFFIX is what makes a second click produce something new. Without it a reader pressing
    Autogenerate twice gets the same sentence, which reads as the button having failed.
  */
  const purpose = `${pick(purposes)} (${uid().slice(0, 6)})`;

  /*
    REAL ROWS FROM THE LIVE FEED, never an invented id — one the server does not hold is
    refused, which reads as a bug in the form rather than in the generator. ONE id per line, as
    the request requires: an Item line's type is the server's to stamp. Zero-stock rows are
    deliberately eligible: asking for what is not there is what the module is for.
  */
  const itemLines: RequisitionSlipLineInput[] = sample(items, 2).map((item) => ({
    preference: LINE_PREFERENCE.item,
    inventoryItemTypeId: null,
    inventoryItemId: item.id,
    requestedQuantity: quantity(),
    remarks: null,
  }));

  const typeLines: RequisitionSlipLineInput[] = sample(itemTypes, 1).map((type) => ({
    preference: LINE_PREFERENCE.type,
    inventoryItemTypeId: type.id,
    inventoryItemId: null,
    requestedQuantity: quantity(),
    remarks: 'Any make will do',
  }));

  return {
    purpose,
    notes: 'Generated sample — please verify before submitting.',
    items: [...itemLines, ...typeLines],
  };
}

function quantity(): number {
  return 1 + Math.floor(Math.random() * 12);
}

function pick<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)];
}

/** Up to `count` distinct rows, or everything there is when the feed is shorter. */
function sample<T>(values: readonly T[], count: number): T[] {
  const pool = [...values];
  const taken: T[] = [];

  while (pool.length > 0 && taken.length < count) {
    taken.push(pool.splice(Math.floor(Math.random() * pool.length), 1)[0]);
  }

  return taken;
}
